import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ludus/ui'
import { disconnectSource, SourceApiError } from '@/lib/api/sources.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'

interface DisconnectDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipelineId: string
  /** The board as the operator knows it, so the sentence names what is being let go. */
  externalRef: string
  onDisconnected: () => Promise<unknown>
}

/**
 * What a disconnect does is stated before it happens, because the answer is not
 * the obvious one: the cards stay, the stages the source brought only go dark
 * where nobody stands on them, and reconnecting the same board brings them back.
 * The one thing that does not come back is the credential.
 *
 * A refusal comes back as a sentence rather than an error — a sweep mid-page is
 * something to wait out, not something that went wrong.
 */
export function DisconnectDialog({
  open,
  onOpenChange,
  pipelineId,
  externalRef,
  onDisconnected,
}: DisconnectDialogProps) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState<'not_connected' | 'sync_in_flight' | null>(null)
  const [error, setError] = useState<SourceApiError | null>(null)

  const run = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    setRefused(null)
    try {
      const result = await disconnectSource(pipelineId)
      if (!result.disconnected) {
        setRefused(result.reason)
        return
      }
      await onDisconnected()
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof SourceApiError ? err : null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('pages.pipelines.connect.disconnect.title', {
              defaultValue: 'Disconnect the source',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.pipelines.connect.disconnect.description', {
              board: externalRef,
              defaultValue: 'This pipeline stops mirroring {{board}}.',
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-muted">
            <li>
              {t('pages.pipelines.connect.disconnect.cards', {
                defaultValue: 'Every card stays, exactly where it is.',
              })}
            </li>
            <li>
              {t('pages.pipelines.connect.disconnect.stages', {
                defaultValue:
                  'The stages the source brought go dark — except those still holding cards, which stay.',
              })}
            </li>
            <li>
              {t('pages.pipelines.connect.disconnect.reconnect', {
                defaultValue: 'Connecting the same board again brings those stages back.',
              })}
            </li>
            <li>
              {t('pages.pipelines.connect.disconnect.credential', {
                defaultValue: 'The access token is destroyed — reconnecting asks for it again.',
              })}
            </li>
          </ul>

          {refused !== null && (
            <p className="rounded-md border border-danger/40 bg-danger/5 p-2.5 text-xs text-danger">
              {refused === 'sync_in_flight'
                ? t('pages.pipelines.connect.disconnect.refusedSync', {
                    defaultValue:
                      'A synchronisation is running right now. Let it finish, then disconnect.',
                  })
                : t('pages.pipelines.connect.disconnect.refusedNotConnected', {
                    defaultValue: 'This pipeline has no source connected.',
                  })}
            </p>
          )}

          {error !== null && (
            <p className="text-xs text-danger">
              {apiErrorMessage(error, t, 'pages.pipelines.connect.disconnect')}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('pages.pipelines.connect.disconnect.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={run} disabled={busy}>
            {t('pages.pipelines.connect.disconnect.confirm', { defaultValue: 'Disconnect' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
